import { noop } from 'lodash';
import { createContext, useContext, useState } from 'react';

import {
  attributionTokenStorage,
  hostedIdStorage,
} from '@utils/storagePropertyManagement';

type HostedAttributionContextType = {
  attributionToken?: string;
  hostedId?: string;
  setAttributionToken: (token: string) => void;
  setHostedId: (id: string) => void;
  clearAttribution: () => void;
};

const HostedAttributionContext = createContext<HostedAttributionContextType>({
  attributionToken: undefined,
  hostedId: undefined,
  setAttributionToken: noop,
  setHostedId: noop,
  clearAttribution: noop,
});

export const useHostedAttribution = () => {
  const context = useContext(HostedAttributionContext);
  if (!context) {
    throw new Error(
      'useHostedAttribution must be used within a HostedAttributionProvider'
    );
  }
  return context;
};

export const HostedAttributionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [attributionToken, setAttributionTokenState] = useState<
    string | undefined
  >(() => attributionTokenStorage.get() ?? undefined);

  const [hostedId, setHostedIdState] = useState<string | undefined>(
    () => hostedIdStorage.get() ?? undefined
  );

  const setAttributionToken = (token: string) => {
    attributionTokenStorage.set(token);
    setAttributionTokenState(token);
  };

  const setHostedId = (id: string) => {
    hostedIdStorage.set(id);
    setHostedIdState(id);
  };

  const clearAttribution = () => {
    attributionTokenStorage.remove();
    hostedIdStorage.remove();
    setAttributionTokenState(undefined);
    setHostedIdState(undefined);
  };

  return (
    <HostedAttributionContext
      value={{
        attributionToken,
        hostedId,
        setAttributionToken,
        setHostedId,
        clearAttribution,
      }}
    >
      {children}
    </HostedAttributionContext>
  );
};
